import { useQuery } from '@/src/hooks/useQuery';
import { Button, Wizard } from '@amzn/awsui-components-react';
import WatchGuideLink from '../common/WatchGuideLink';
import _ from 'lodash';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { EventCreationType } from '../../constants/shared/event-type';
import { EVENT_DETAILS_ROUTES } from '../../routes';
import { useApi } from '../../store/api.context';
import { useChallenges } from '../../store/challenge.context';
import { useEditEvent } from '../../store/edit-event.context';
import { useFlashbars } from '../../store/flashbar.context';
import { useSplitPanel } from '../../store/split-panel.context';
import { useToolPanel } from '../../store/tool-panel.context';
import { Event, JamEventRequest } from '../../types/Event';
import { i18nKeys } from '../../utils/i18n.utils';
import { eventOwnerGuideVideo } from '../../utils/jam-urls';
import { isEmpty } from '../../utils/list.utils';
import { preProdLogger } from '../../utils/log.utils';
import { fromPlainObject } from '../../utils/mapper.utils';
import NewEventChallenges from '../common/CampaignEventComponents/NewEventChallenges';
import { ConfirmModal } from '../common/ConfirmModal';
import JamSpinner from '../common/JamSpinner/JamSpinner';
import TeamSettings from './eventDetailsSections/Summary/TeamSettings';
import { NewEventSteps } from './EventModel';
import AttendanceAndTesting from './newEventSteps/AttendanceAndTesting';
import NewEventDetails from './newEventSteps/NewEventDetails';
import OwnersAndPermissions from './newEventSteps/OwnersAndPermissions';
import ReviewAndCreate from './newEventSteps/ReviewAndCreate';

type NewEventProps = { paramLoadingDelayMS?: number };

const NewEvent: React.FC<NewEventProps> = ({ paramLoadingDelayMS = 500 }) => {
  const { t } = useTranslation();
  const { eventsApi } = useApi();
  const { addErrorFlashbar, clearFlashbars } = useFlashbars();
  const { newEventMode, toggleNewEvent, newEditedEvent, destroyEdits, populateWithUrlParams } = useEditEvent();
  const { toggleShowSplitPanel, toggleSplitPanel, showSplitPanel } = useSplitPanel();
  const { toggleShowToolPanel, toggleToolPanel, showToolPanel } = useToolPanel();
  const [confirmCancelVisible, setConfirmCancelVisible] = useState(false);
  const [notifyUsersAdded, setNotifyUsersAdded] = useState(false);
  const [populatedFields, setPopulatedFields] = useState<string[]>([]);
  const cameFromAIAssistant = useMemo(() => new URLSearchParams(location.search).get('aigenerated') === 'true', []);
  const [activeStepIndex, setActiveStepIndex] = useState(cameFromAIAssistant ? 5 : 0);
  const [loading, setLoading] = useState(false);
  const [doneDelayBeforeParamLoading, doneDelayBeforeParamLoadingSet] = useState(false);
  const history = useHistory();
  const { queryParams } = useQuery();
  const { challengeWrappers, getChallenges } = useChallenges();
  const [preloadingChallenges] = useState(false);
  const timeoutRef = useRef(0);

  useEffect(() => {
    if (!newEventMode) {
      toggleNewEvent();
    }
    // TODO: This component has a race condition between this and it's children which can make the
    // params coming from the cloned event be overriden. This delay gives time for the children to
    // settle their state before adding the data coming from the cloned event, but a proper fix would
    // be to centraliza access to the new event object on this component so there's no risk of race
    // conditions
    // https://i.amazon.com/issues/JAM-12289
    timeoutRef.current = setTimeout(() => doneDelayBeforeParamLoadingSet(true), paramLoadingDelayMS);
    return () => clearTimeout(timeoutRef.current);
  }, []);

  useEffect(() => {
    if (!challengeWrappers) {
      void getChallenges(false, false, true);
    }
  }, [challengeWrappers]);

  useEffect(() => {
    if (
      doneDelayBeforeParamLoading &&
      newEventMode &&
      queryParams &&
      challengeWrappers &&
      challengeWrappers.length > 0
    ) {
      const fieldsPopulated = populateWithUrlParams(queryParams);
      if (!isEmpty(fieldsPopulated)) {
        setPopulatedFields(fieldsPopulated);
        if (queryParams.get('aigenerated') !== 'true') {
          setNotifyUsersAdded(true);
        }
      }
    }
  }, [newEventMode, challengeWrappers, doneDelayBeforeParamLoading]);

  const handleCancel = () => {
    destroyEdits();
    history.push('/my-events');
  };

  const promptModal = () => {
    setConfirmCancelVisible(true);
  };

  let isCurrentSectionValid: () => Promise<boolean>;
  const validationHandler = (validateSection: () => Promise<boolean>) => {
    isCurrentSectionValid = validateSection;
  };

  const handleCreateEvent = () => {
    if (newEditedEvent) {
      setLoading(true);
      const jamEventRequest = fromPlainObject(newEditedEvent, JamEventRequest) as JamEventRequest;

      // Set eventCreationType for custom events (non-AI generated events)
      if (!cameFromAIAssistant) {
        jamEventRequest.eventCreationType = EventCreationType.CUSTOM_EVENT;
      }

      eventsApi
        .createJamEventRequest(jamEventRequest)
        .then((res: Event) => {
          // Update event tags if needed
          if (res && !_.isEqual(res.tags, newEditedEvent.tags)) {
            res.tags = newEditedEvent.tags;
            eventsApi.updateTags(res).catch((err) => {
              preProdLogger(err);
            });
          }
          history.replace(EVENT_DETAILS_ROUTES.Summary.resolve(res?.name));
          destroyEdits();
          setLoading(false);
        })
        .catch((err) => {
          window.scrollTo(0, 0);
          setLoading(false);
          preProdLogger(err);
        });
    }
  };

  const handleActiveStepChange = (index: number) => {
    // Allow user to go back to previous steps in-case they wish to change something
    if (index < activeStepIndex) {
      setActiveStepIndex(index);
      return;
    }
    isCurrentSectionValid()
      .then((isValid) => {
        if (isValid) {
          handleActiveStepIndex(index);
        }
      })
      .catch((error) => {
        preProdLogger('Could not complete validation.', error);
      });
  };

  const handleActiveStepIndex = (stepIndex: number) => {
    window.scrollTo(0, 0);
    const previousStep = stepsDictionary[(stepIndex - 1) as unknown as NewEventSteps];
    // Check for validation errors
    if (previousStep?.isValid && !previousStep.isValid() && previousStep.getErrors) {
      const errorList = previousStep.getErrors();
      clearFlashbars();
      requestAnimationFrame(() => {
        addErrorFlashbar(t(i18nKeys.newEvent.errors.message, { errors: errorList.join(', ') }));
      });
    } else {
      // If not on challenge step, hide challenge selection aid
      // Make sure to update this if we ever add additional steps or uncover a better way to track the activeStepIndex per step
      if (stepIndex !== NewEventSteps.CHALLENGES && (showSplitPanel || showToolPanel)) {
        if (showSplitPanel) {
          toggleShowSplitPanel(false);
          toggleSplitPanel(false);
        }
        if (showToolPanel) {
          toggleShowToolPanel(false);
          toggleToolPanel(false);
        }
      }
      setActiveStepIndex(stepIndex);
    }
  };

  /**
   * Add steps here to keep active step number inline with wizard
   */
  const stepsDictionary: {
    [key in NewEventSteps]: {
      title: string;
      content: ReactNode;
      info?: ReactNode;
      isValid?: () => boolean;
      getErrors?: () => string[];
    };
  } = {
    [NewEventSteps.EVENT_DETAILS]: {
      title: t(i18nKeys.newEvent.steps.eventDetails),
      info: <WatchGuideLink videoUrl={eventOwnerGuideVideo} title={t(i18nKeys.newEvent.eventCreationGuideLink)} />,
      content: newEditedEvent && <NewEventDetails validationHandler={validationHandler} target={newEditedEvent} />,
      isValid: () => {
        return (
          !_.isEmpty(newEditedEvent?.title) &&
          !_.isEmpty(newEditedEvent?.audienceType) &&
          !_.isEmpty(newEditedEvent?.startDate) &&
          !_.isEmpty(newEditedEvent?.endDate)
        );
      },
      getErrors: () => {
        const errors: string[] = [];
        if (
          _.isEmpty(newEditedEvent?.title) &&
          _.isEmpty(newEditedEvent?.audienceType) &&
          _.isEmpty(newEditedEvent?.startDate) &&
          _.isEmpty(newEditedEvent?.endDate)
        ) {
          errors.push(t(i18nKeys.newEvent.errors.allFieldsEmpty));
        } else {
          if (_.isEmpty(newEditedEvent?.title)) {
            errors.push(t(i18nKeys.newEvent.errors.title));
          }
          if (_.isEmpty(newEditedEvent?.audienceType)) {
            errors.push(t(i18nKeys.newEvent.errors.audience));
          }
          if (_.isEmpty(newEditedEvent?.startDate)) {
            errors.push(t(i18nKeys.newEvent.errors.startDate));
          }
          if (_.isEmpty(newEditedEvent?.endDate)) {
            errors.push(t(i18nKeys.newEvent.errors.duration));
          }
        }
        return errors;
      },
    },
    [NewEventSteps.ATTENDANCE_AND_TESTING]: {
      title: t(i18nKeys.newEvent.steps.attendanceAndTesting),
      content: newEditedEvent && <AttendanceAndTesting target={newEditedEvent} validationHandler={validationHandler} />,
      isValid: () => {
        return (
          !_.isEmpty(newEditedEvent?.minExpectedParticipants) && !_.isEmpty(newEditedEvent?.maxExpectedParticipants)
        );
      },
    },
    [NewEventSteps.TEAM_SETTINGS]: {
      title: t(i18nKeys.newEvent.steps.teamAssignmentSettings),
      content: newEditedEvent && <TeamSettings target={newEditedEvent} validationHandler={validationHandler} />,
      isValid: () => {
        return (
          newEditedEvent?.formTeamsMinsBeforeEventStart != null && newEditedEvent?.formTeamsMinsBeforeEventStart >= 0
        );
      },
      getErrors: () => {
        const errors: string[] = [];
        if (!newEditedEvent?.formTeamsMinsBeforeEventStart || newEditedEvent?.formTeamsMinsBeforeEventStart < 0) {
          errors.push(t(i18nKeys.newEvent.errors.teamFormingStartTime));
        }
        return errors;
      },
    },
    [NewEventSteps.OWNERS_AND_PERMISSIONS]: {
      title: t(i18nKeys.newEvent.steps.ownersAndPermissions),
      content: newEditedEvent && <OwnersAndPermissions target={newEditedEvent} validationHandler={validationHandler} />,
    },
    [NewEventSteps.CHALLENGES]: {
      title: t(i18nKeys.newEvent.steps.challenges),
      content: newEditedEvent && <NewEventChallenges target={newEditedEvent} validationHandler={validationHandler} />,
      isValid: () => {
        return !_.isEmpty(newEditedEvent?.challengeDescriptors);
      },
      getErrors: () => {
        const errors: string[] = [];
        if (_.isEmpty(newEditedEvent?.challengeDescriptors)) {
          errors.push(t(i18nKeys.events.eventDetails.messages.errors.selectChallenges));
        }
        return errors;
      },
    },
    [NewEventSteps.REVIEW_AND_CREATE]: {
      title: t(i18nKeys.newEvent.steps.reviewAndCreate),
      content: newEditedEvent && <ReviewAndCreate target={newEditedEvent} navigate={handleActiveStepIndex} />,
    },
  };

  return (
    <>
      <ConfirmModal
        visible={confirmCancelVisible}
        message={t(i18nKeys.newEvent.modal.message)}
        cancelBtnLabel={t(i18nKeys.newEvent.modal.cancel)}
        confirmBtnLabel={t(i18nKeys.newEvent.modal.confirm)}
        onConfirm={handleCancel}
        onCancel={() => setConfirmCancelVisible(false)}
      />
      <ConfirmModal
        title={t(i18nKeys.events.templateUrls.modalTitle)}
        visible={notifyUsersAdded}
        message={
          <>
            {t(i18nKeys.events.templateUrls.message)}
            <div>
              <ul>
                {populatedFields.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </div>
          </>
        }
        confirmBtnLabel={t(i18nKeys.events.templateUrls.modalOk)}
        onConfirm={() => setNotifyUsersAdded(false)}
        onDismiss={() => setNotifyUsersAdded(false)}
        data-testid="notify-users-added-modal"
      />
      {!preloadingChallenges && (
        <Wizard
          className="mb-12"
          onCancel={() => promptModal()}
          i18nStrings={{
            stepNumberLabel: (stepNumber) => `Step ${stepNumber}`,
            collapsedStepsLabel: (stepNumber, stepsCount) => `Step ${stepNumber} of ${stepsCount}`,
            cancelButton: t(i18nKeys.general.cancel),
            previousButton: t(i18nKeys.general.previous),
            nextButton: t(i18nKeys.general.next),
            submitButton: t(i18nKeys.events.eventDetails.buttons.createEventRequest),
            optional: t(i18nKeys.general.optional),
          }}
          activeStepIndex={activeStepIndex}
          steps={Object.keys(stepsDictionary).map((stepKey: string) => {
            const step = stepsDictionary[stepKey as unknown as NewEventSteps];
            return {
              title: step.title,
              content: step.content,
              info: step.info,
            };
          })}
          secondaryActions={
            cameFromAIAssistant && <Button onClick={() => history.goBack()}>Go back to AI assistant</Button>
          }
          onNavigate={({ detail }) => {
            handleActiveStepChange(detail.requestedStepIndex);
          }}
          isLoadingNextStep={loading}
          onSubmit={handleCreateEvent}
          data-testid="newEventWizard"
        />
      )}
      {preloadingChallenges && <JamSpinner />}
    </>
  );
};
export default NewEvent;
